import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { BlockWithRevisions, MISSED_REASON_LABELS, MissedReason } from '@/types';
import styles from './Block.module.css';

interface BlockProps {
  block: BlockWithRevisions;
  startHour: number;
  hourHeight: number;
  onClick?: () => void;
  columnIndex?: number;
  totalColumns?: number;
}

// Block category to CSS class mapping
const categoryClasses: Record<string, string> = {
  'deep-work': styles.deepWork,
  shallow: styles.shallow,
  personal: styles.personal,
  meeting: styles.meeting,
  urgent: styles.urgent,
};

// Helper to get position and size
function getBlockStyle(startTime: number, duration: number, startHour: number, hourHeight: number) {
  const startMinutes = startHour * 60;
  const top = ((startTime - startMinutes) / 60) * hourHeight;
  const height = (duration / 60) * hourHeight;
  return { top, height: Math.max(height, 30) }; // Min height of 30px
}

// Format time from minutes
function formatTime(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const date = new Date();
  date.setHours(hours, mins, 0, 0);
  return format(date, 'h:mm a');
}

// Detect duration change from originalDuration field
function getDurationChange(block: BlockWithRevisions) {
  if (block.originalDuration === undefined || block.originalDuration === block.duration) {
    return null;
  }

  const difference = block.duration - block.originalDuration;
  const isExtended = difference > 0;

  return {
    originalDuration: block.originalDuration,
    currentDuration: block.duration,
    difference,
    isExtended,
    changedAt: block.durationChangedAt,
  };
}

// Format duration change for badge
function formatDurationBadge(difference: number) {
  const sign = difference > 0 ? '+' : '';
  return `${sign}${difference}m`;
}

// Format duration change timestamp for tooltip
function formatChangeTime(changedAt: { toDate?: () => Date } | Date | undefined) {
  if (!changedAt) return '';
  // Handle Firestore Timestamp (has toDate method) or regular Date
  if ('toDate' in changedAt && typeof changedAt.toDate === 'function') {
    return format(changedAt.toDate(), 'h:mm a');
  }
  if (changedAt instanceof Date) {
    return format(changedAt, 'h:mm a');
  }
  return '';
}

export function Block({
  block,
  startHour,
  hourHeight,
  onClick,
  columnIndex = 0,
  totalColumns = 1,
}: BlockProps) {
  const { top, height } = getBlockStyle(block.startTime, block.duration, startHour, hourHeight);

  // Calculate horizontal positioning for overlapping blocks
  const columnWidth = 100 / totalColumns;
  const leftPercent = columnIndex * columnWidth;
  const columnStyle =
    totalColumns > 1
      ? {
          left: `${leftPercent}%`,
          width: `calc(${columnWidth}% - 4px)`, // Small gap between columns
          right: 'auto',
        }
      : {};

  // Determine category class
  const categoryClass = block.categoryId ? categoryClasses[block.categoryId] : styles.deepWork;

  // Get duration change info for in-block indicators
  const durationChange = useMemo(() => getDurationChange(block), [block]);

  // Calculate position of original boundary line (as percentage of block height)
  const originalBoundaryPosition = useMemo(() => {
    if (!durationChange) return null;
    // For extended blocks: line shows where original end was (original/current)
    // For shortened blocks: line is outside the block, so we calculate differently
    if (durationChange.isExtended) {
      return (durationChange.originalDuration / durationChange.currentDuration) * 100;
    }
    return null; // For shortened, we render outside the block
  }, [durationChange]);

  // Tooltip state for duration indicator
  const [showDurationTooltip, setShowDurationTooltip] = useState(false);

  // Deleted block rendering
  if (block.status === 'cancelled') {
    return (
      <motion.div
        className={`${styles.block} ${categoryClass} ${styles.ghost} ${styles.deleted}`}
        style={{ top, height, borderColor: block.color, ...columnStyle }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 0.4, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.2 }}
        role="button"
        tabIndex={0}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
        aria-label={`Cancelled: ${block.title}`}
      >
        <div className={styles.blockContent}>
          <span
            className={`${styles.title} ${styles.strikethrough} ${!block.title ? styles.untitled : ''}`}
          >
            {block.title || '(untitled)'}
          </span>
          <span className={styles.time}>
            {formatTime(block.startTime)} - {formatTime(block.startTime + block.duration)}
          </span>
        </div>
        <div className={styles.deleteX} aria-hidden="true">
          X
        </div>
      </motion.div>
    );
  }

  return (
    <>
      {/* Shortened marker - shows original end position outside the block */}
      {durationChange && !durationChange.isExtended && (
        <motion.div
          className={styles.shortenedMarker}
          style={{
            top: top + height, // Position at current block end
            height: (Math.abs(durationChange.difference) / 60) * hourHeight,
          }}
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{ opacity: 1, scaleY: 1 }}
          transition={{ duration: 0.3 }}
          aria-hidden="true"
        >
          <span className={styles.shortenedBadge}>
            {formatDurationBadge(durationChange.difference)}
          </span>
        </motion.div>
      )}

      {/* Main block */}
      <motion.div
        className={`${styles.block} ${categoryClass} ${
          block.status === 'completed' ? styles.completed : ''
        } ${block.status === 'missed' ? styles.missed : ''} ${block.isMIT ? styles.mitBlock : ''}`}
        style={{ top, height, borderColor: block.color, ...columnStyle }}
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        layout
        layoutId={`block-${block.id}`}
        role="button"
        tabIndex={0}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
        aria-label={`${block.title}, ${formatTime(block.startTime)} to ${formatTime(block.startTime + block.duration)}`}
      >
        {/* MIT indicator */}
        {block.isMIT && (
          <motion.div
            className={styles.mitIndicator}
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 20, delay: 0.1 }}
            aria-label={`Most Important Task${block.mitOrder ? ` #${block.mitOrder}` : ''}`}
          >
            <span className={styles.mitStar}>⭐</span>
            {block.mitOrder && <span className={styles.mitOrder}>{block.mitOrder}</span>}
          </motion.div>
        )}

        {/* Extended region indicator - shows original boundary and stripe pattern */}
        {durationChange && durationChange.isExtended && originalBoundaryPosition !== null && (
          <>
            {/* Dashed line at original end position */}
            <div
              className={styles.originalBoundary}
              style={{ top: `${originalBoundaryPosition}%` }}
              aria-hidden="true"
            />
            {/* Striped extended region */}
            <div
              className={styles.extendedRegion}
              style={{
                top: `${originalBoundaryPosition}%`,
                height: `${100 - originalBoundaryPosition}%`,
              }}
              onMouseEnter={() => setShowDurationTooltip(true)}
              onMouseLeave={() => setShowDurationTooltip(false)}
              aria-hidden="true"
            >
              <span className={styles.durationBadge}>
                {formatDurationBadge(durationChange.difference)}
              </span>
            </div>
            {/* Tooltip */}
            <AnimatePresence>
              {showDurationTooltip && (
                <motion.div
                  className={styles.durationTooltip}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.15 }}
                >
                  Extended from {durationChange.originalDuration}m to{' '}
                  {durationChange.currentDuration}m
                  {durationChange.changedAt && (
                    <span className={styles.tooltipTime}>
                      {' '}
                      at {formatChangeTime(durationChange.changedAt)}
                    </span>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        <div className={styles.blockContent}>
          <span className={`${styles.title} ${!block.title ? styles.untitled : ''}`}>
            {block.title || '(untitled)'}
          </span>
          <span className={styles.time}>
            {formatTime(block.startTime)} - {formatTime(block.startTime + block.duration)}
          </span>
        </div>

        {/* Completion indicator */}
        {block.status === 'completed' && (
          <motion.div
            className={styles.completedBadge}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 20 }}
          >
            Done
          </motion.div>
        )}

        {/* Missed indicator */}
        {block.status === 'missed' && (
          <>
            <motion.div
              className={styles.missedBadge}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 20 }}
            >
              Missed
            </motion.div>
            {block.missedReason && (
              <span className={styles.missedReason}>
                {MISSED_REASON_LABELS[block.missedReason]}
              </span>
            )}
          </>
        )}
      </motion.div>
    </>
  );
}

export default Block;
